import type { DocumentItem } from './model.js';

export type SelectionMode = 'single' | 'multiple';

export interface SelectionConfig {
  enabled: boolean;
  mode: SelectionMode;
  /** Are files selectable? Default `true`. */
  files: boolean;
  /** Are folders selectable? Default `true`. */
  folders: boolean;
  /** Cap on how many items may be selected at once. */
  max?: number;
}

export const DEFAULT_SELECTION_CONFIG: SelectionConfig = {
  enabled: false,
  mode: 'multiple',
  files: true,
  folders: true,
};

/**
 * Selection is stored by id, not index, so it survives navigation, sorting and
 * filtering — the thing users notice immediately when a library gets it wrong.
 */
export interface SelectionState {
  ids: ReadonlySet<string>;
  /** Where a future shift-click range starts. */
  anchorId: string | null;
}

export const EMPTY_SELECTION: SelectionState = { ids: new Set(), anchorId: null };

export function isSelectable(item: DocumentItem, config: SelectionConfig): boolean {
  if (!config.enabled) return false;
  return item.type === 'folder' ? config.folders : config.files;
}

function capped(ids: string[], config: SelectionConfig): Set<string> {
  if (config.max !== undefined && ids.length > config.max) {
    // Keep the most recently added, which is what a user reaching the cap expects.
    return new Set(ids.slice(ids.length - config.max));
  }
  return new Set(ids);
}

/** Toggle one item. In `single` mode this replaces the selection. */
export function toggleSelection(
  state: SelectionState,
  item: DocumentItem,
  config: SelectionConfig,
): SelectionState {
  if (!isSelectable(item, config)) return state;

  if (config.mode === 'single') {
    const already = state.ids.has(item.id) && state.ids.size === 1;
    return already ? { ids: new Set(), anchorId: null } : { ids: new Set([item.id]), anchorId: item.id };
  }

  const next = [...state.ids];
  const at = next.indexOf(item.id);
  if (at >= 0) {
    next.splice(at, 1);
    return { ids: new Set(next), anchorId: item.id };
  }
  next.push(item.id);
  return { ids: capped(next, config), anchorId: item.id };
}

/** Replace the selection with exactly this item. */
export function selectOnly(state: SelectionState, item: DocumentItem, config: SelectionConfig): SelectionState {
  if (!isSelectable(item, config)) return state;
  return { ids: new Set([item.id]), anchorId: item.id };
}

/**
 * Shift-click / shift-arrow: select everything between the anchor and `item`
 * in the *currently visible* order, so the range matches what the user sees.
 */
export function selectRange(
  state: SelectionState,
  item: DocumentItem,
  visible: readonly DocumentItem[],
  config: SelectionConfig,
): SelectionState {
  if (config.mode === 'single') return selectOnly(state, item, config);
  if (state.anchorId === null) return toggleSelection(state, item, config);

  const from = visible.findIndex((v) => v.id === state.anchorId);
  const to = visible.findIndex((v) => v.id === item.id);
  if (from < 0 || to < 0) return toggleSelection(state, item, config);

  const [start, end] = from <= to ? [from, to] : [to, from];
  const next = [...state.ids];
  for (let i = start; i <= end; i++) {
    const candidate = visible[i];
    if (!candidate || !isSelectable(candidate, config)) continue;
    if (!next.includes(candidate.id)) next.push(candidate.id);
  }
  return { ids: capped(next, config), anchorId: state.anchorId };
}

/** Select every selectable item currently visible. */
export function selectAll(
  state: SelectionState,
  visible: readonly DocumentItem[],
  config: SelectionConfig,
): SelectionState {
  if (!config.enabled || config.mode === 'single') return state;
  const ids = visible.filter((v) => isSelectable(v, config)).map((v) => v.id);
  return { ids: capped(ids, config), anchorId: state.anchorId };
}

export function clearSelection(): SelectionState {
  return { ids: new Set(), anchorId: null };
}
